import { Button, Modal, ModalBody, ModalHeader } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faGear, faPause, faPlay, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useContext, useEffect, useState } from 'react';
import marketSignalApi from '@/modules/market-signals/api/market-signal-api';
import dayjs from 'dayjs';
import MarketSignalStreamConfig from '@/modules/market-signals/components/market-signals/market-signal-stream-config';
import articleListStyles from '@/shared/components/flash-highlight.module.scss';
import MarketSignalScrollerEntry from '@/modules/market-signals/components/market-signals/scroller/market-signal-scroller-entry';
import MarketSignalStreamSelector from '@/modules/market-signals/components/market-signals/market-signal-stream-selector';
import MarketSignalTopicBadges from '@/modules/market-signals/components/market-signals/topics/market-signal-topic-badges';
import MarketSignalAssistant from '@/modules/market-signals/components/market-signals/assistant/market-signal-assistant';
import MarketSignalAlarmConfigModal from '@/modules/market-signals/components/market-signals/alarms/market-signal-alarm-config-modal';
import { MarketSignalStreamAnalysisTopic } from '@/modules/market-signals/model/market-signals/market-signal-stream-analysis';
import MarketSignalTopicModal from '@/modules/market-signals/components/market-signals/topics/market-signal-topic-modal';
import MarketSignalModal from '@/modules/market-signals/components/market-signals/market-signal-modal';
import { useTimer } from '@/shared/hooks/export';
import { MarketSignalContext } from '@/modules/market-signals/contexts/market-signal-context';
import MarketSignalAlarmModal from '@/modules/market-signals/components/market-signals/alarms/market-signal-alarm-modal';
import styles from '../market-signals.module.css';
import MarketSignalScrollerPanel from '@/modules/market-signals/components/market-signals/scroller/market-signal-scroller-panel';
import MarketSignalSearchModal from '@/modules/market-signals/components/market-signals/search/market-signal-search-modal';
import { copyText } from '@/modules/market-signals/components/market-signals/market-signal-copy-resource';

export default function MarketSignalScroller() {
  const [showConfig, setShowConfig] = useState<boolean>(false);
  const [showAlarms, setShowAlarms] = useState<boolean>(false);
  const [showAssistant, setShowAssistant] = useState<boolean>(false);
  const [assistantQuestion, setAssistantQuestion] = useState<string>();
  const [showAlarmConfig, setShowAlarmConfig] = useState<boolean>(false);
  const [selectedTopic, setSelectedTopic] = useState<MarketSignalStreamAnalysisTopic>();
  const [mentionedIds, setMentionedIds] = useState<MarketSignalStreamAnalysisTopic[]>([]);
  const {
    alarms,
    setAlarms,
    alarmsForSignal,
    selectedAlarm,
    setSelectedAlarm,
    setStreams,
    selectedStream,
    setSelectedStream,
  } = useContext(MarketSignalContext);
  const [paused, setPaused] = useState<boolean>(false);
  const [missedUpdates, setMissedUpdates] = useState<number>(0);
  const [showSearch, setShowSearch] = useState<boolean>(false);

  const fetchAlarms = () => {
    marketSignalApi.getAlarms(0, 25).then(value => setAlarms(value.content));
  };

  const refreshStreams = () => {
    if (!paused) {
      marketSignalApi.getStreams().then(value => {
        setStreams(value.content);
      });
    }
    fetchAlarms();
  };

  useEffect(
    () => {
      refreshStreams();
    }, // eslint-disable-next-line
    [],
  );

  useEffect(() => {
    if (!selectedStream) return;
    setMissedUpdates(0);
  }, [selectedStream, paused]);

  useTimer(
    90_000,
    () => {
      if (!selectedStream) return;
      fetchAlarms();
    },
    false,
    'ms-reload',
  );

  const flash = (date: string) => {
    if (dayjs(date).isAfter(dayjs().subtract(5, 'minute'))) return articleListStyles.flashArticle;
    return '';
  };

  const triggerQuestion = (s: string) => {
    setAssistantQuestion(s);
    setShowAssistant(true);
  };

  const newAlarms = alarms?.filter(a => !a.acknowledged) || [];

  return (
    <div className={'h-100 d-flex flex-column'}>
      <div className={'input-group input-group-sm mb-1'}>
        <Button
          variant={'outline-secondary'}
          onClick={() => setShowAlarms(true)}
          disabled={newAlarms.length === 0}
        >
          <FontAwesomeIcon icon={faBell} />
          <sup>
            {newAlarms.length > 0 ? (
              <span className={`badge ${styles.flashingAlarmBackground}`}>{newAlarms.length}</span>
            ) : (
              <span className={'badge bg-success'}>0</span>
            )}
          </sup>
        </Button>

        <Button
          variant={paused ? 'outline-success' : 'outline-secondary'}
          onClick={event => setPaused(!paused)}
        >
          {!paused && <FontAwesomeIcon icon={faPause} />}
          {paused && <FontAwesomeIcon icon={faPlay} />}
        </Button>
        {/*<Button variant={'outline-tertiary'} onClick={() => setShowAssistant(!showAssistant)}>
          <FontAwesomeIcon icon={faCommentDots} />
        </Button>
        */}
        <MarketSignalStreamSelector onStreamSelect={s => setSelectedStream(s?.code)} />

        <Button variant={'secondary'} onClick={() => setShowConfig(true)}>
          <FontAwesomeIcon icon={faGear} />
        </Button>
        <Button variant={'outline-secondary'} onClick={() => setShowAlarmConfig(true)}>
          <FontAwesomeIcon icon={faBell} />
          <sup>
            <FontAwesomeIcon icon={faGear} />
          </sup>
        </Button>
        <Button variant={'warning'} onClick={() => setShowSearch(true)}>
          <FontAwesomeIcon icon={faSearch} />
        </Button>
      </div>

      <div className={'mb-1'}>
        {selectedStream && (
          <MarketSignalTopicBadges
            selectedStream={selectedStream}
            onClick={setSelectedTopic}
            onLoaded={setMentionedIds}
            showTooltips={!selectedTopic}
          />
        )}
      </div>

      {paused && (
        <div className={'alert alert-warning small p-2 d-flex justify-content-between'}>
          <span>
            Live updates paused, <span className={'font-monospace'}>{missedUpdates}</span> new
            items.
          </span>
          <a
            href={'#a'}
            onClick={event => {
              event.stopPropagation();
              event.preventDefault();
              setPaused(false);
            }}
          >
            <FontAwesomeIcon icon={faPlay} /> Resume live updates
          </a>
        </div>
      )}

      {selectedStream && (
        <MarketSignalScrollerPanel
          selectedStream={selectedStream}
          paused={paused}
          renderSignal={(value, index) => (
            <>
              {selectedAlarm && value._id === selectedAlarm.signalId && (
                <div
                  className="position-absolute top-50 start-50 translate-middle bg-black bg-opacity-50"
                  style={{ padding: '20%' }}
                >
                  <Button
                    variant="secondary"
                    className="p-5 text-nowrap"
                    onClick={() => {
                      navigator.clipboard
                        .writeText(copyText(value))
                        .then(() => setSelectedAlarm(undefined));
                    }}
                  >
                    <span className="h2">Click to copy</span>
                  </Button>
                </div>
              )}
              <div
                key={value._id + '-' + index}
                className={`small  border-top-0  pb-3 ${alarmsForSignal(value._id).length > 0 ? 'bg-danger-subtle' : flash(value.dateCreated)}`}
              >
                <MarketSignalScrollerEntry
                  signal={value}
                  topics={mentionedIds}
                  interactive={true}
                />
              </div>
            </>
          )}
          onMissedUpdate={() => setMissedUpdates(missedUpdates + 1)}
        />
      )}

      <MarketSignalAssistant
        show={showAssistant}
        onClose={() => {
          setAssistantQuestion(undefined);
          setShowAssistant(false);
        }}
        question={assistantQuestion}
      />
      <MarketSignalModal />
      <MarketSignalAlarmModal
        show={showAlarms}
        onClose={() => setShowAlarms(false)}
        alarms={newAlarms}
      />
      <MarketSignalAlarmConfigModal
        show={showAlarmConfig}
        onClose={() => setShowAlarmConfig(false)}
      />
      <MarketSignalTopicModal
        onClose={() => setSelectedTopic(undefined)}
        topic={selectedTopic}
        onTriggerQuestion={triggerQuestion}
      />
      <MarketSignalSearchModal show={showSearch} onClose={() => setShowSearch(false)} />

      <Modal
        show={showConfig}
        size={'lg'}
        onBackdropClick={() => setShowConfig(false)}
        onHide={() => setShowConfig(false)}
      >
        <ModalHeader>
          <h3>Stream Configuration</h3>
        </ModalHeader>
        <ModalBody>
          <MarketSignalStreamConfig
            onClose={() => {
              setShowConfig(false);
              refreshStreams();
            }}
          />
        </ModalBody>
      </Modal>
    </div>
  );
}
